// Push notification helpers — subscribe, unsubscribe, permission negotiation.
//
// Called from the pre-prompt card + Settings page. Handles the awkward
// base64url ↔ Uint8Array conversion that `pushManager.subscribe` requires,
// timezone offset capture, and endpoint sync with the backend.

import api from "./api";

const SW_URL = "/sw.js";

// URL-safe base64 → Uint8Array (browsers reject standard base64 with `+` and `/`
// for the applicationServerKey field).
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Returns { supported, permission, subscribed } — probe the environment
 * so the UI can render the correct state (grant / already on / not
 * supported / iOS-needs-home-screen-first).
 */
export async function pushStatus() {
  if (typeof window === "undefined") return { supported: false };
  const hasSW = "serviceWorker" in navigator;
  const hasPush = "PushManager" in window;
  const hasNotif = "Notification" in window;
  const supported = hasSW && hasPush && hasNotif;
  if (!supported) return { supported: false };

  // iOS 16.4+ requires the PWA to be launched from the Home Screen for
  // push to work at all. `standalone` (Safari) / matchMedia(display-mode)
  // (Chrome) both signal that state.
  const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent);
  const isStandalone =
    (typeof navigator !== "undefined" && navigator.standalone) ||
    (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
  const iosNeedsInstall = isIOS && !isStandalone;

  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      subscribed = !!sub;
    }
  } catch (_) {}

  return {
    supported: true,
    permission: Notification.permission, // 'default' | 'granted' | 'denied'
    subscribed,
    iosNeedsInstall,
  };
}

async function registerSW() {
  const reg = await navigator.serviceWorker.register(SW_URL);
  // Some browsers return a still-installing worker; wait for it to be
  // active before we ask `pushManager.subscribe` (which needs a ready SW).
  if (reg.installing) {
    await new Promise((resolve) => {
      const w = reg.installing;
      w.addEventListener("statechange", () => {
        if (w.state === "activated" || w.state === "installed") resolve();
      });
    });
  }
  return reg;
}

/**
 * Kick off the full subscribe flow. Triggers the browser's native
 * permission prompt (only fires if permission is currently 'default').
 * Returns true if the user is now subscribed on this device.
 */
export async function subscribeToPush() {
  const s = await pushStatus();
  if (!s.supported) throw new Error("Push notifications not supported on this browser");
  if (s.iosNeedsInstall) throw new Error("iOS: install to Home Screen first");

  // 1. Ask for permission (idempotent — browser skips if already granted/denied).
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return false;

  // 2. Fetch VAPID public key from backend.
  const { data } = await api.get("/push/vapid-public-key");
  const applicationServerKey = urlBase64ToUint8Array(data.public_key);

  // 3. Register SW + create browser subscription.
  const reg = await registerSW();
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true, // required by every browser — the user must see every push
    applicationServerKey,
  });

  // 4. POST subscription details to backend (multi-device supported).
  const raw = sub.toJSON();
  const tz_offset_min = -new Date().getTimezoneOffset(); // JS returns inverted sign
  // Read the current app language from the same key the global i18n
  // provider uses. Keeps the backend + the UI in sync so reminders
  // never arrive in a language different from the app the user is
  // looking at.
  const lang = (() => {
    try { return localStorage.getItem("calofay:lang") || "ar"; }
    catch { return "ar"; }
  })();
  await api.post("/push/subscribe", {
    endpoint: raw.endpoint,
    keys: raw.keys,
    tz_offset_min,
    lang,
  });

  return true;
}

/**
 * Cheap in-place language sync — call whenever the user switches app
 * language. Avoids a full resubscribe (which would re-prompt for
 * permission on some browsers) and just updates the stored `lang` on
 * this device's subscription row so future scheduled pushes match.
 */
export async function syncPushLanguage(lang) {
  try {
    if (!("serviceWorker" in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    await api.post("/push/language", { endpoint: sub.endpoint, lang });
  } catch (_) {
    // Silent — worst case the next scheduled push arrives in the old
    // language and the user re-clicks their language toggle later.
  }
}

/**
 * Unsubscribe on this device + remove endpoint from the backend.
 * Silent no-op if we weren't subscribed.
 */
export async function unsubscribeFromPush() {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    try { await api.post("/push/unsubscribe", { endpoint }); } catch (_) {}
  } catch (_) {}
}

/** Wrapper used by the Settings 'Send test' button. */
export async function sendTestPush() {
  const { data } = await api.post("/push/test");
  return data;
}

/** Preferences (per-category toggles). Thin wrapper over the API. */
export async function getPushPreferences() {
  const { data } = await api.get("/push/preferences");
  return data;
}
export async function updatePushPreferences(patch) {
  const { data } = await api.patch("/push/preferences", patch);
  return data;
}
